import { useState } from 'react';
import api from '@/lib/api';
import useUiStore from '@/store/uiStore';
import { useAuth } from '@/contexts/AuthContext';
import { ChevronDown, ChevronUp, Zap, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

function FormattedBlock({ text }) {
  if (!text) return null;
  const parts = text.split('\n');
  return (
    <div className="space-y-1.5">
      {parts.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1.5" />;
        let formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>');
        formatted = formatted.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-muted rounded text-[11px] font-mono text-foreground">$1</code>');
        if (/^\s*[-*]\s/.test(line)) {
          const content = line.replace(/^\s*[-*]\s/, '');
          let fc = content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>');
          fc = fc.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-muted rounded text-[11px] font-mono text-foreground">$1</code>');
          return (
            <div key={i} className="flex gap-1.5 ml-1">
              <span className="text-cobalt mt-1 shrink-0 text-[8px]">&#9679;</span>
              <span dangerouslySetInnerHTML={{ __html: fc }} />
            </div>
          );
        }
        const numMatch = line.match(/^\s*(\d+)[.)]\s/);
        if (numMatch) {
          const content = line.replace(/^\s*\d+[.)]\s/, '');
          let fc = content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>');
          fc = fc.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-muted rounded text-[11px] font-mono text-foreground">$1</code>');
          return (
            <div key={i} className="flex gap-1.5 ml-1">
              <span className="text-muted-foreground font-mono text-[10px] mt-0.5 shrink-0 w-3 text-right">{numMatch[1]}.</span>
              <span dangerouslySetInnerHTML={{ __html: fc }} />
            </div>
          );
        }
        return <p key={i} dangerouslySetInnerHTML={{ __html: formatted }} />;
      })}
    </div>
  );
}

export default function ExplainTab({ nodeLabel, nodeDescription }) {
  const [summary, setSummary] = useState('');
  const [deepDive, setDeepDive] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const { openUpgradeModal } = useUiStore();
  const { user, refreshUser } = useAuth();

  const fetchExplanation = async () => {
    setLoading(true);
    setSummary('');
    setDeepDive('');
    setExpanded(false);
    try {
      const res = await api.post('/ai/explain', { nodeLabel, nodeDescription: nodeDescription || '' });
      setSummary(res.data.summary);
      setDeepDive(res.data.deepDive);
      setHasGenerated(true);
      refreshUser();
    } catch (err) {
      if (err.response?.status === 403) openUpgradeModal();
      else setSummary('Failed to load explanation.');
    } finally { setLoading(false); }
  };

  // Reset when node changes
  if (hasGenerated && !summary && !loading) {
    setHasGenerated(false);
  }

  return (
    <div className="p-4" data-testid="explain-tab">
      {/* Credits display for all users */}
      <div className="flex items-center gap-1.5 mb-3 px-2 py-1.5 bg-muted/40 rounded-md" data-testid="explain-credits">
        <Zap className="w-3 h-3 text-cobalt" strokeWidth={1.5} />
        <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
          {user?.credits ?? 0} credit{user?.credits !== 1 ? 's' : ''} left
        </span>
      </div>

      {/* Show generate button if not yet generated */}
      {!hasGenerated && !loading && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="font-body text-sm text-muted-foreground mb-4">
            Get an AI-powered explanation of <span className="font-medium text-foreground">"{nodeLabel}"</span>
          </p>
          <Button 
            onClick={fetchExplanation}
            className="bg-cobalt text-white hover:bg-cobalt/90"
            data-testid="generate-explanation-btn"
          >
            Generate Explanation
          </Button>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 py-8 justify-center" data-testid="explain-loading">
          <div className="spinner" />
          <span className="font-body text-sm text-muted-foreground">Analyzing...</span>
        </div>
      )}
      
      {hasGenerated && !loading && (
        <>
          <div className="mb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Summary</span>
            <div className="font-body text-sm leading-relaxed mt-1.5" data-testid="explain-summary">
              <FormattedBlock text={summary} />
            </div>
          </div>
          {deepDive && (
            <div>
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-cobalt hover:text-cobalt/80 transition-colors"
                data-testid="explain-deepdive-toggle"
              >
                Deep Dive {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {expanded && (
                <div className="mt-2 font-body text-sm leading-relaxed text-muted-foreground" data-testid="explain-deepdive-content">
                  <FormattedBlock text={deepDive} />
                </div>
              )}
            </div>
          )}
          
          {/* Regenerate button */}
          <div className="mt-4 pt-4 border-t border-border">
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchExplanation}
              className="w-full text-xs"
              data-testid="regenerate-explanation-btn"
            >
              Regenerate
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
